import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

// Palette Supermont
const C = {
  primary: '#1565c0',
  primaryDark: '#0d47a1',
  primaryLight: '#1976d2',
  accent: '#42a5f5',
  bg: '#ffffff',
  bgLight: '#f4f6f9',
  border: '#dee2e6',
  gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  textMain: '#1a1a1a',
  textSub: '#555',
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats/dashboard', { params: { brand: BRAND } });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Nunito, Inter, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <div style={{ color: '#333' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Nunito, Inter, sans-serif' }}>
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} user={user} />
      <MainContent activeTab={activeTab} message={message} setMessage={setMessage} />
    </div>
  );
};

const Sidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const navigate = useNavigate();

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
    { id: 'sessions', label: 'Sessions', icon: 'fa-calendar-alt' },
    { id: 'pdvs', label: 'Points de Vente', icon: 'fa-store' },
    { id: 'stocks', label: 'Stocks', icon: 'fa-boxes', external: true },
    { id: 'daily-quotas', label: 'Quotas Journaliers', icon: 'fa-calendar-day', external: true },
    { id: 'prizes', label: 'Lots', icon: 'fa-gift' },
    { id: 'injection', label: 'Injection', icon: 'fa-syringe' },
    { id: 'participants', label: 'Participants', icon: 'fa-users', external: true },
    { id: 'users', label: 'Utilisateurs', icon: 'fa-user-cog' },
  ];

  const handleMenuClick = (item) => {
    if (item.external) {
      navigate(`/${item.id}`);
    } else {
      setActiveTab(item.id);
    }
  };

  return (
    <div style={{
      width: '250px',
      background: 'rgba(13, 71, 161, 0.95)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${C.border}`
    }}>
      <div style={{ padding: '24px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
          <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: '#ffffff', overflow: 'hidden', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
          </div>
        </div>
        <p style={{ color: '#fff', margin: '4px 0 0', fontWeight: 'bold', textAlign: 'center', fontSize: '14px' }}>Les Perles de Vie</p>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '11px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>Administration</p>
      </div>
      <div style={{ padding: '16px', flex: 1 }}>
        {menuItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleMenuClick(item)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              background: activeTab === item.id ? `rgba(66, 165, 245, 0.25)` : 'transparent',
              textAlign: 'left',
              color: activeTab === item.id ? C.accent : 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderLeft: activeTab === item.id ? `3px solid ${C.accent}` : '3px solid transparent'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== item.id) {
                e.currentTarget.style.background = `rgba(66, 165, 245, 0.1)`;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== item.id) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <i className={`fas ${item.icon}`} style={{ width: '20px', textAlign: 'center' }}></i>
            {item.label}
          </button>
        ))}

        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.04)',
            color: '#ff4d4f',
            border: '1px solid rgba(255, 77, 79, 0.7)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '8px'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 77, 79, 0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; }}
        >
          <i className="fas fa-sign-out-alt"></i>
          Déconnexion
        </button>
      </div>

      <div style={{ padding: '16px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: C.gradient,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 600
          }}>
            {user?.username?.charAt(0) || 'A'}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{user?.username || 'Admin'}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>{user?.role || ''}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainContent = ({ activeTab, message, setMessage }) => {
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedPOS, setSelectedPOS] = useState('all');

  const loadStats = async (cityId = null, dateFrom = null, pdvId = null) => {
    try {
      const params = { brand: BRAND };
      if (cityId && cityId !== 'all') params.city_id = parseInt(cityId);
      if (dateFrom && dateFrom !== 'all') params.date_from = dateFrom;
      if (pdvId && pdvId !== 'all') params.pdv_id = pdvId;
      const response = await api.get('/admin/stats/dashboard', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    }
  };

  const loadHourlyData = async () => {
    try {
      const params = { brand: BRAND };
      const response = await api.get('/admin/stats/hourly', { params });
      setHourlyData(response.data);
    } catch (error) {
      console.error('Error loading hourly data:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadStats(selectedCity, selectedDate, selectedPOS);
      loadHourlyData();
    }
  }, [activeTab, selectedCity, selectedDate, selectedPOS]);

  if (activeTab === 'sessions') return <SessionsContent message={message} setMessage={setMessage} />;
  if (activeTab === 'pdvs') return <PDVsContent message={message} setMessage={setMessage} />;
  if (activeTab === 'prizes') return <PrizesContent message={message} setMessage={setMessage} />;
  if (activeTab === 'injection') return <InjectionContent message={message} setMessage={setMessage} />;
  if (activeTab === 'users') return <UsersContent message={message} setMessage={setMessage} />;

  if (activeTab !== 'dashboard') {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '80px 40px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <i className="fas fa-tools" style={{ fontSize: '64px', color: C.primary, marginBottom: '24px' }}></i>
          <h3 style={{ color: '#1a1a1a', marginBottom: '12px', fontSize: '20px', fontWeight: 600 }}>Module en développement</h3>
          <p style={{ color: '#555', fontSize: '14px' }}>Ce module sera bientôt disponible.</p>
        </div>
      </div>
    );
  }

  const pdvList = stats?.by_pos ? [...new Set(stats.by_pos.map(p => p.name))] : [];

  const getFromDate = (preset) => {
    if (preset === 'all' || !preset) return null;
    const today = new Date();
    if (preset === 'today') return today.toISOString().split('T')[0];
    if (preset === 'week') { const d = new Date(today); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; }
    if (preset === 'month') { const d = new Date(today); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; }
    return preset;
  };

  const handleDateChange = (e) => {
    setSelectedDate(getFromDate(e.target.value) || e.target.value);
  };

  const selectStyle = {
    padding: '8px 16px',
    border: `1px solid ${C.border}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: '#333',
    background: '#ffffff',
    cursor: 'pointer',
    outline: 'none'
  };

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? '#fff2f0' : '#f6ffed',
          border: `1px solid ${message.type === 'error' ? '#ffccc7' : '#b7eb8f'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#cf1322' : '#389e0d',
          fontSize: '14px',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      {/* Dashboard Header */}
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-gem" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>Dashboard — Les Perles de Vie</h1>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} style={selectStyle}>
            <option value="all">Toutes les villes</option>
            {stats?.cities?.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
          </select>
          <select value={typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/) ? 'custom' : selectedDate} onChange={handleDateChange} style={selectStyle}>
            <option value="all">Toutes les dates</option>
            <option value="today">Aujourd'hui</option>
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="custom">Date spécifique</option>
          </select>
          {(typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) && (
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={selectStyle} />
          )}
          <select value={selectedPOS} onChange={(e) => setSelectedPOS(e.target.value)} style={selectStyle}>
            <option value="all">Tous les points de vente</option>
            {pdvList.map(pdv => <option key={pdv} value={pdv}>{pdv}</option>)}
          </select>
        </div>
      </div>

      {/* KPI Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <KPICard title="Participants" value={stats?.summary?.total_participations || 0} icon="fa-user-group" color="#1890ff" />
        <KPICard title="Gagnants" value={stats?.summary?.total_winners || 0} icon="fa-medal" color="#52c41a" />
        <KPICard title="Lots Gagnés" value={stats?.summary?.total_prizes || 0} icon="fa-gift" color="#faad14" />
        <KPICard title="Bouteilles Vendues" value={stats?.summary?.total_bottles || 0} icon="fa-bottle-water" color="#eb2f96" />
        <KPICard title="Points de Vente Actifs" value={stats?.summary?.active_points || 0} icon="fa-store" color="#722ed1" />
      </div>

      {/* Two Column Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PrizeDistributionSection stats={stats} />
        <ParticipationModeSection stats={stats} />
      </div>

      <PerformanceSection stats={stats} />
      <CommercialPressureSection stats={stats} hourlyData={hourlyData} message={message} setMessage={setMessage} />
    </div>
  );
};

const KPICard = ({ title, value, icon, color }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'all 0.3s' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
      <div style={{ width: '52px', height: '52px', background: color + '20', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '24px' }}></i>
      </div>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>{value.toLocaleString()}</div>
    <div style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>{title}</div>
  </div>
);

const PrizeDistributionSection = ({ stats }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', background: 'rgba(114, 46, 209, 0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-solid fa-gift" style={{ color: '#722ed1', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>Répartition des Lots</h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {stats?.prizes && Object.entries(stats.prizes).map(([prizeType, data], idx) => (
        <div key={prizeType} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f4f6f9', borderRadius: '12px', border: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', background: getColor(idx) + '20', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fa-solid fa-gem" style={{ color: getColor(idx), fontSize: '20px' }}></i>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{prizeType}</div>
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: getColor(idx) }}>{data.won}</div>
        </div>
      ))}
    </div>
  </div>
);

const ParticipationModeSection = ({ stats }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', background: 'rgba(24, 144, 255, 0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-solid fa-share-nodes" style={{ color: '#1890ff', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>Mode de Participation</h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <ModeCard icon="fab fa-whatsapp" label="WhatsApp" value={stats?.engagement?.whatsapp || 0} rate={stats?.engagement?.whatsapp_rate || 0} color="#25D366" />
      <ModeCard icon="fa-solid fa-mobile-screen" label="Direct" value={stats?.engagement?.direct || 0} rate={stats?.engagement?.direct_rate || 0} color="#1890ff" />
      <ModeCard icon="fa-solid fa-qrcode" label="QR Code" value={stats?.engagement?.qr_code || 0} rate={stats?.engagement?.qr_code_rate || 0} color="#FF6B35" />
    </div>
  </div>
);

const ModeCard = ({ icon, label, value, rate, color }) => (
  <div style={{ padding: '16px', background: '#f4f6f9', borderRadius: '12px', border: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '44px', height: '44px', background: color + '20', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={icon} style={{ color: color, fontSize: '22px' }}></i>
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{rate}% du total</div>
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color: color }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#666' }}>Participations</div>
    </div>
  </div>
);

const PerformanceSection = ({ stats }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', background: 'rgba(250, 173, 20, 0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-solid fa-store" style={{ color: '#faad14', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>Performance par Point de Vente</h3>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #dee2e6' }}>
            {['Point de Vente', 'Ville', 'Inscrits', 'Gagnants', 'Perdants', 'Bouteilles', 'Taux'].map(h => (
              <th key={h} style={{ padding: '16px', textAlign: h.length > 8 ? 'left' : 'center', color: '#555', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stats?.by_pos && stats.by_pos.map((pos, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ padding: '16px', color: '#1a1a1a', fontSize: '14px', fontWeight: 500 }}>{pos.name}</td>
              <td style={{ padding: '16px', color: '#555', fontSize: '14px' }}>{pos.city}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#1890ff', fontSize: '14px', fontWeight: 600 }}>{pos.inscrits || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#52c41a', fontSize: '14px', fontWeight: 600 }}>{pos.gagnants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#ff7875', fontSize: '14px', fontWeight: 600 }}>{pos.perdants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#faad14', fontSize: '14px', fontWeight: 600 }}>{pos.bouteilles || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center' }}>
                <span style={{
                  padding: '6px 16px', borderRadius: '20px',
                  background: pos.performance >= 80 ? 'rgba(82, 196, 26, 0.15)' : pos.performance >= 50 ? 'rgba(250, 173, 20, 0.15)' : 'rgba(255, 77, 79, 0.15)',
                  color: pos.performance >= 80 ? '#389e0d' : pos.performance >= 50 ? '#d4930a' : '#cf1322',
                  fontWeight: 600, fontSize: '14px', display: 'inline-block'
                }}>
                  {pos.performance || 0}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CommercialPressureSection = ({ stats, hourlyData, message, setMessage }) => {
  const [selectedPDV, setSelectedPDV] = useState('all');
  const [selectedDisplay, setSelectedDisplay] = useState('all');
  const [hoveredCell, setHoveredCell] = useState(null);

  const getAllDates = () => {
    if (!hourlyData) return [];
    if (Array.isArray(hourlyData.all_dates) && hourlyData.all_dates.length > 0) return [...hourlyData.all_dates].sort().reverse();
    if (!hourlyData.by_pdv) return [];
    const dates = new Set();
    hourlyData.by_pdv.forEach(pdv => Object.keys(pdv.dates || {}).forEach(date => dates.add(date)));
    return Array.from(dates).sort().reverse();
  };

  const getAllPDVs = () => {
    if (!hourlyData || !hourlyData.by_pdv) return [];
    return hourlyData.by_pdv.map(pdv => ({ id: pdv.pdv_id, name: pdv.pdv_name }));
  };

  const getFilteredData = () => {
    if (!hourlyData || !hourlyData.by_pdv) return { dates: [], pdvs: [] };
    let pdvs = hourlyData.by_pdv;
    if (selectedPDV !== 'all') pdvs = pdvs.filter(pdv => pdv.pdv_id === parseInt(selectedPDV));
    const hourFilter = (hour) => {
      if (selectedDisplay === 'all') return true;
      const selectedHour = parseInt(selectedDisplay);
      return !isNaN(selectedHour) && hour === selectedHour;
    };
    const dates = getAllDates();
    return {
      dates,
      pdvs: pdvs.map(pdv => {
        const pdvData = { id: pdv.pdv_id, name: pdv.pdv_name, totals: {}, hourlyBreakdown: {} };
        dates.forEach(date => {
          const dateData = (pdv.dates || {})[date];
          if (dateData && dateData.hourly) {
            let total = 0;
            dateData.hourly.forEach(h => { if (hourFilter(h.hour)) total += h.bottles || 0; });
            pdvData.totals[date] = total;
            pdvData.hourlyBreakdown[date] = dateData.hourly.filter(h => hourFilter(h.hour));
          } else {
            pdvData.totals[date] = 0;
            pdvData.hourlyBreakdown[date] = [];
          }
        });
        pdvData.total = Object.values(pdvData.totals).reduce((sum, val) => sum + val, 0);
        return pdvData;
      })
    };
  };

  const filteredData = getFilteredData();
  const allPDVs = getAllPDVs();
  const allDates = filteredData.dates;
  const columnTotals = {};
  allDates.forEach(date => { columnTotals[date] = filteredData.pdvs.reduce((sum, pdv) => sum + (pdv.totals[date] || 0), 0); });
  const grandTotal = Object.values(columnTotals).reduce((sum, val) => sum + val, 0);

  const thStyle = {
    padding: '14px 8px', textAlign: 'center',
    background: C.gradient, color: '#fff', minWidth: '60px', fontWeight: 600, fontSize: '13px'
  };

  return (
    <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '40px', height: '40px', background: 'rgba(19, 194, 194, 0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fa-solid fa-chart-line" style={{ color: '#13c2c2', fontSize: '18px' }}></i>
        </div>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>Pression Commerciale</h3>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={selectedDisplay} onChange={(e) => setSelectedDisplay(e.target.value)}
          style={{ padding: '8px 12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', color: '#333', fontSize: '13px' }}>
          <option value="all">Toutes les heures</option>
          {Array.from({ length: 14 }, (_, i) => i + 8).map(hour => <option key={hour} value={hour.toString()}>{hour}h</option>)}
        </select>
        <select value={selectedPDV} onChange={(e) => setSelectedPDV(e.target.value)}
          style={{ padding: '8px 12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', color: '#333', fontSize: '13px', minWidth: '150px' }}>
          <option value="all">Tous les points de vente</option>
          {allPDVs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}</option>)}
        </select>
      </div>

      {allDates.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Données non disponibles</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', padding: '14px 12px', position: 'sticky', left: 0, zIndex: 10 }}>Point de vente</th>
                {allDates.map(date => {
                  const d = new Date(date);
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <th key={date} style={{ ...thStyle, background: isToday ? 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)' : C.gradient, border: isToday ? '2px solid #ffd700' : 'none' }}>
                      <div>{String(d.getDate()).padStart(2,'0')}/{String(d.getMonth()+1).padStart(2,'0')}</div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>{d.toLocaleDateString('fr-FR',{weekday:'short'})}</div>
                    </th>
                  );
                })}
                <th style={thStyle}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.pdvs.map(pdv => (
                <tr key={pdv.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '14px 12px', color: '#1a1a1a', position: 'sticky', left: 0, background: '#f4f6f9', fontWeight: 500, fontSize: '13px', borderRight: '1px solid #dee2e6' }}>{pdv.name}</td>
                  {allDates.map(date => {
                    const value = pdv.totals[date] || 0;
                    const cellKey = `${pdv.id}-${date}`;
                    return (
                      <td key={date} onMouseEnter={() => setHoveredCell(cellKey)} onMouseLeave={() => setHoveredCell(null)}
                        style={{ padding: '12px 8px', textAlign: 'center', color: value > 0 ? '#1a1a1a' : '#bbb', fontWeight: value > 0 ? 600 : 400, fontSize: '14px', background: hoveredCell === cellKey ? 'rgba(21,101,192,0.08)' : 'transparent', cursor: value > 0 ? 'help' : 'default' }}>
                        <span style={{ display: 'inline-block', padding: value > 0 ? '4px 8px' : '4px', borderRadius: '4px', background: value > 0 ? C.gradient : 'transparent', color: value > 0 ? '#fff' : 'inherit', boxShadow: value > 0 ? '0 2px 8px rgba(21,101,192,0.3)' : 'none' }}>
                          {value}
                        </span>
                      </td>
                    );
                  })}
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#1565c0', fontWeight: 700, fontSize: '15px' }}>{pdv.total}</td>
                </tr>
              ))}
              <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f0f6ff' }}>
                <td style={{ padding: '14px 12px', color: '#1565c0', fontWeight: 700, position: 'sticky', left: 0, background: '#e3f2fd', fontSize: '13px', textTransform: 'uppercase', borderRight: '1px solid #dee2e6' }}>Total</td>
                {allDates.map(date => {
                  const total = columnTotals[date] || 0;
                  const isToday = new Date(date).toDateString() === new Date().toDateString();
                  return (
                    <td key={date} style={{ padding: '14px 8px', textAlign: 'center', color: isToday ? '#1565c0' : '#333', fontWeight: 700, fontSize: '15px' }}>{total}</td>
                  );
                })}
                <td style={{ padding: '14px 12px', textAlign: 'center', color: '#1565c0', fontWeight: 800, fontSize: '16px' }}>{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const getColor = (idx) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'];
  return colors[idx % colors.length];
};

// ============ SESSIONS ============
const SessionsContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sessions', { params: { brand: BRAND } });
      setSessions(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des sessions' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    const newStatus = session.status === 'active' ? 'pending' : 'active';
    try {
      if (newStatus === 'active') {
        await api.put(`/admin/sessions/${sessionId}/activate`, {}, { params: { brand: BRAND } });
      } else {
        await api.put(`/admin/sessions/${sessionId}/deactivate`, {}, { params: { brand: BRAND } });
      }
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, status: newStatus } : s));
      setMessage({ type: 'success', text: newStatus === 'active' ? 'Session activée' : 'Session désactivée' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du changement de statut' });
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) return;
    try {
      await api.delete(`/admin/sessions/${sessionId}`, { params: { brand: BRAND } });
      setSessions(sessions.filter(s => s.id !== sessionId));
      setMessage({ type: 'success', text: 'Session supprimée avec succès' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      active: { label: 'En cours', color: '#389e0d', bg: 'rgba(82, 196, 26, 0.15)' },
      completed: { label: 'Terminée', color: '#555', bg: '#f0f0f0' },
      pending: { label: 'Planifiée', color: '#1565c0', bg: '#e3f2fd' },
      cancelled: { label: 'Annulée', color: '#cf1322', bg: 'rgba(255, 77, 79, 0.12)' }
    };
    const c = cfg[status] || cfg.pending;
    return <span style={{ padding: '6px 16px', borderRadius: '20px', background: c.bg, color: c.color, fontSize: '13px', fontWeight: 600 }}>{c.label}</span>;
  };

  if (loading) return <div style={{ flex: 1, padding: '30px', textAlign: 'center', color: '#333' }}>Chargement...</div>;

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-calendar-alt" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>Sessions</h1>
        </div>
        <button onClick={() => { setSelectedSession(null); setShowModal(true); }}
          style={{ padding: '12px 24px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fas fa-plus"></i>Nouvelle Session
        </button>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                {['Point de Vente', 'Session', 'Date & Horaire', 'Mode', 'Statut', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '16px', textAlign: 'left', color: '#555', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '16px', color: '#1a1a1a', fontSize: '14px', fontWeight: 600 }}>{session.pdv_name || 'Non assigné'}</td>
                  <td style={{ padding: '16px', color: '#333', fontSize: '14px' }}>{session.name}</td>
                  <td style={{ padding: '16px', color: '#555', fontSize: '13px' }}>
                    {new Date(session.start_time).toLocaleDateString('fr-FR')} {new Date(session.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(session.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', background: '#e3f2fd', color: '#1565c0', fontSize: '11px', fontWeight: 600 }}>
                      {session.gain_mode || 'auto'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>{getStatusBadge(session.status)}</td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button onClick={() => handleToggleSession(session.id)}
                        style={{ padding: '6px 12px', background: session.status === 'active' ? 'rgba(82,196,26,0.12)' : '#f9f9f9', color: session.status === 'active' ? '#389e0d' : '#cf1322', border: `1px solid ${session.status === 'active' ? '#52c41a' : '#ff4d4f'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        <i className={`fas fa-${session.status === 'active' ? 'toggle-on' : 'toggle-off'}`}></i>
                      </button>
                      <button onClick={() => { setSelectedSession(session); setShowModal(true); }}
                        style={{ padding: '6px 12px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button onClick={() => handleDeleteSession(session.id)}
                        style={{ padding: '6px 12px', background: 'rgba(255,77,79,0.08)', color: '#cf1322', border: '1px solid rgba(255,77,79,0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <SessionModal session={selectedSession} onClose={() => setShowModal(false)} onSave={async () => { await loadSessions(); setShowModal(false); }} />
      )}
    </div>
  );
};

const SessionModal = ({ session, onClose, onSave }) => {
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [formData, setFormData] = useState({
    name: session?.name || '',
    pdv_id: session?.pdv_id || '',
    start_time: session?.start_time ? new Date(session.start_time).toISOString().slice(0, 16) : '',
    end_time: session?.end_time ? new Date(session.end_time).toISOString().slice(0, 16) : '',
    gain_mode: session?.gain_mode || 'auto',
    ratio_value: session?.ratio_value || 5,
    percentage_value: session?.percentage_value || 80,
    stock_allocations: session?.stock_allocations || {}
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pdvsRes, prizesRes] = await Promise.all([
          api.get('/admin/pdv', { params: { brand: BRAND } }),
          api.get('/admin/prizes', { params: { brand: BRAND } })
        ]);
        setPdvs(pdvsRes.data);
        setPrizes(prizesRes.data);
        if (!session && Object.keys(formData.stock_allocations).length === 0) {
          const initialStock = {};
          prizesRes.data.forEach(p => { initialStock[p.id] = p.prize_type.includes('try_again') ? 999 : 10; });
          setFormData(prev => ({ ...prev, stock_allocations: initialStock }));
        }
      } catch (error) { console.error('Error loading data:', error); }
    };
    loadData();
  }, []);

  const inputStyle = { width: '100%', padding: '12px 16px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: '#fff', outline: 'none' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stock_allocations = Object.entries(formData.stock_allocations).map(([prizeId, quantity]) => ({
      prize_config_id: parseInt(prizeId),
      initial_quantity: parseInt(quantity)
    }));
    const sessionData = {
      name: formData.name, pdv_id: parseInt(formData.pdv_id),
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      gain_mode: formData.gain_mode, stock_allocations
    };
    if (formData.gain_mode === 'ratio') sessionData.ratio_value = formData.ratio_value;
    if (formData.gain_mode === 'pourcentage') sessionData.percentage_value = formData.percentage_value;
    try {
      if (session) {
        await api.put(`/admin/sessions/${session.id}`, { ...sessionData, brand: BRAND });
      } else {
        await api.post('/admin/sessions', { ...sessionData, brand: BRAND });
      }
      onSave(sessionData);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création de la session');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>{session ? 'Modifier la Session' : 'Nouvelle Session'}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Point de Vente *</label>
            <select value={formData.pdv_id} onChange={(e) => setFormData({ ...formData, pdv_id: parseInt(e.target.value) })} required style={inputStyle}>
              <option value="">Sélectionner un point de vente</option>
              {pdvs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name} ({pdv.city?.name})</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Nom de la session *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Session Promo Janvier" required style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Début *</label>
              <input type="datetime-local" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} required style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Fin *</label>
              <input type="datetime-local" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} required style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Mode de gain</label>
            <select value={formData.gain_mode} onChange={(e) => setFormData({ ...formData, gain_mode: e.target.value })} style={inputStyle}>
              <option value="auto">Automatique</option>
              <option value="ratio">Ratio</option>
              <option value="force">Forcé</option>
              <option value="pourcentage">Pourcentage</option>
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Allocation des Stocks</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {prizes.filter(p => !p.prize_type.includes('try_again')).map(prize => (
                <div key={prize.id} style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', background: '#f4f6f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ width: '24px', height: '24px', background: prize.color, borderRadius: '50%' }}></div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>
                      {prize.name}<div style={{ fontSize: '11px', color: '#666' }}>{prize.prize_label}</div>
                    </div>
                  </div>
                  <input type="number" value={formData.stock_allocations[prize.id] || 0}
                    onChange={(e) => setFormData({ ...formData, stock_allocations: { ...formData.stock_allocations, [prize.id]: parseInt(e.target.value) || 0 } })}
                    min="0" required style={{ ...inputStyle, padding: '8px 12px' }} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px 24px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
            <button type="submit" style={{ flex: 1, padding: '12px 24px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>{session ? 'Modifier' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ PDVS ============
const PDVsContent = ({ message, setMessage }) => {
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPDV, setSelectedPDV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', code: '', address: '', city_id: '', has_big_screen: false });

  useEffect(() => { loadPDVs(); loadCities(); }, []);

  const loadPDVs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pdv', { params: { brand: BRAND } });
      setPdvs(response.data);
    } catch (error) { setMessage({ type: 'error', text: 'Erreur lors du chargement des points de vente' }); }
    finally { setLoading(false); }
  };

  const loadCities = async () => {
    try { const r = await api.get('/admin/cities'); setCities(r.data); } catch {}
  };

  const handleSavePDV = async () => {
    try {
      if (selectedPDV) { await api.put(`/admin/pdv/${selectedPDV.id}`, formData); setMessage({ type: 'success', text: 'PDV mis à jour' }); }
      else { await api.post('/admin/pdv', formData); setMessage({ type: 'success', text: 'PDV créé' }); }
      setShowModal(false); loadPDVs();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la sauvegarde' }); }
  };

  const handleDeletePDV = async (pdvId) => {
    if (!window.confirm('Supprimer ce point de vente ?')) return;
    try { await api.delete(`/admin/pdv/${pdvId}`); setPdvs(pdvs.filter(p => p.id !== pdvId)); setMessage({ type: 'success', text: 'PDV supprimé' }); }
    catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' }); }
  };

  const inputStyle = { width: '100%', padding: '10px', border: '1px solid #dee2e6', borderRadius: '8px', background: '#fff', color: '#1a1a1a', fontSize: '14px' };

  if (loading) return <div style={{ flex: 1, padding: '32px', color: '#333' }}>Chargement...</div>;

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-store" style={{ color: '#fff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>Points de Vente</h1>
        </div>
        <button onClick={() => { setSelectedPDV(null); setFormData({ name: '', code: '', address: '', city_id: '', has_big_screen: false }); setShowModal(true); }}
          style={{ padding: '12px 24px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fas fa-plus"></i>Nouveau PDV
        </button>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
              {['Nom', 'Code', 'Ville', 'Adresse', 'Statut', 'Actions'].map(h => (
                <th key={h} style={{ padding: '16px', textAlign: 'left', color: '#555', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pdvs.map((pdv) => (
              <tr key={pdv.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '16px', color: '#1a1a1a', fontWeight: 600 }}>{pdv.name}</td>
                <td style={{ padding: '16px', color: '#333' }}>{pdv.code}</td>
                <td style={{ padding: '16px', color: '#333' }}>{pdv.city?.name || '-'}</td>
                <td style={{ padding: '16px', color: '#555' }}>{pdv.address || '-'}</td>
                <td style={{ padding: '16px' }}>
                  <span style={{ padding: '6px 16px', borderRadius: '20px', background: pdv.is_active ? 'rgba(82,196,26,0.12)' : 'rgba(255,77,79,0.12)', color: pdv.is_active ? '#389e0d' : '#cf1322', fontSize: '13px', fontWeight: 600 }}>
                    {pdv.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setSelectedPDV(pdv); setFormData({ name: pdv.name, code: pdv.code, address: pdv.address || '', city_id: pdv.city_id?.toString() || '', has_big_screen: pdv.has_big_screen || false }); setShowModal(true); }}
                      style={{ padding: '6px 12px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button onClick={() => handleDeletePDV(pdv.id)}
                      style={{ padding: '6px 12px', background: 'rgba(255,77,79,0.08)', color: '#cf1322', border: '1px solid rgba(255,77,79,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
            <h2 style={{ color: '#1565c0', marginBottom: '24px', fontSize: '20px' }}>{selectedPDV ? 'Modifier le PDV' : 'Nouveau Point de Vente'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div><label style={{ color: '#333', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: 600 }}>Nom *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ color: '#333', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: 600 }}>Code *</label><input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ color: '#333', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: 600 }}>Ville *</label>
                <select value={formData.city_id} onChange={(e) => setFormData({ ...formData, city_id: e.target.value })} style={inputStyle}>
                  <option value="">Sélectionner une ville</option>
                  {cities.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
                </select>
              </div>
              <div><label style={{ color: '#333', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: 600 }}>Adresse</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={formData.has_big_screen} onChange={(e) => setFormData({ ...formData, has_big_screen: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                <label style={{ color: '#333', fontSize: '14px', margin: 0 }}>Écran géant</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Annuler</button>
              <button onClick={handleSavePDV} style={{ padding: '10px 20px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>{selectedPDV ? 'Mettre à jour' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ PRIZES ============
const PrizesContent = ({ message, setMessage }) => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrize, setEditingPrize] = useState(null);

  useEffect(() => { loadPrizes(); }, []);

  const loadPrizes = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/prizes', { params: { brand: BRAND } }); setPrizes(r.data); }
    catch (error) { setMessage({ type: 'error', text: 'Erreur lors du chargement des lots' }); }
    finally { setLoading(false); }
  };

  const handleSave = async (prizeData) => {
    try {
      await api.put(`/admin/prizes/${editingPrize.id}`, prizeData);
      setMessage({ type: 'success', text: 'Lot mis à jour avec succès' });
      setEditingPrize(null); loadPrizes();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' }); }
  };

  if (loading) return <div style={{ padding: '32px', color: '#333' }}>Chargement...</div>;

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-gift" style={{ color: '#fff', fontSize: '18px' }}></i>
        </div>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>Configuration des Lots</h1>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
              {['Segment', 'Émotion', 'Lot', 'Couleur', 'Injection Admin', 'Actions'].map(h => (
                <th key={h} style={{ padding: '16px', textAlign: 'left', color: '#555', fontSize: '12px', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prizes.map((prize) => (
              <tr key={prize.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '16px', fontWeight: 600, color: '#1a1a1a' }}>{prize.segment_order}</td>
                <td style={{ padding: '16px', color: '#333' }}>{prize.name}</td>
                <td style={{ padding: '16px', color: '#333' }}>{prize.prize_label}</td>
                <td style={{ padding: '16px' }}><div style={{ width: '30px', height: '30px', background: prize.color, borderRadius: '50%' }}></div></td>
                <td style={{ padding: '16px' }}>
                  <span style={{ padding: '4px 12px', borderRadius: '20px', background: prize.requires_admin_injection ? '#e3f2fd' : '#f0f0f0', color: prize.requires_admin_injection ? '#1565c0' : '#888', fontSize: '12px', fontWeight: 600 }}>
                    {prize.requires_admin_injection ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td style={{ padding: '16px' }}>
                  <button onClick={() => setEditingPrize(prize)}
                    style={{ padding: '6px 16px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    <i className="fas fa-edit"></i> Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingPrize && <PrizeEditModal prize={editingPrize} onClose={() => setEditingPrize(null)} onSave={handleSave} />}
    </div>
  );
};

const PrizeEditModal = ({ prize, onClose, onSave }) => {
  const [formData, setFormData] = useState({ name: prize.name, prize_label: prize.prize_label, description: prize.description || '', color: prize.color, icon: prize.icon || '' });
  const inputStyle = { width: '100%', padding: '12px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', background: '#fff', color: '#1a1a1a' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>Modifier le Lot</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Émotion</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required style={inputStyle} /></div>
          <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Lot</label><input type="text" value={formData.prize_label} onChange={(e) => setFormData({ ...formData, prize_label: e.target.value })} required style={inputStyle} /></div>
          <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Description</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" style={inputStyle} /></div>
          <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' }}>Couleur</label><input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} style={{ ...inputStyle, height: '50px' }} /></div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
            <button type="submit" style={{ flex: 1, padding: '12px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ INJECTION ============
const InjectionContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedPrize, setSelectedPrize] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, pRes] = await Promise.all([
          api.get('/admin/sessions', { params: { brand: BRAND } }),
          api.get('/admin/prizes', { params: { brand: BRAND } })
        ]);
        setSessions(sRes.data); setPrizes(pRes.data);
      } catch {}
    };
    load();
  }, []);

  const handleInject = async () => {
    if (!selectedSession || !selectedPrize) { setMessage({ type: 'error', text: 'Veuillez sélectionner une session et un lot' }); return; }
    setLoading(true);
    try {
      await api.post('/admin/inject-win', { session_id: parseInt(selectedSession), prize_type: selectedPrize, brand: BRAND });
      setMessage({ type: 'success', text: 'Gain injecté avec succès !' });
      setSelectedSession(''); setSelectedPrize('');
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'injection' }); }
    finally { setLoading(false); }
  };

  const selectStyle = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #dee2e6', fontSize: '14px', color: '#1a1a1a', background: '#fff' };

  return (
    <div style={{ flex: 1, padding: '30px', overflowY: 'auto' }}>
      <h2 style={{ color: '#1a1a1a', marginBottom: '24px', fontSize: '28px', fontWeight: 600 }}>
        <i className="fas fa-syringe me-2" style={{ color: C.primary }}></i>Injection de gains forcés
      </h2>
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ color: C.primary, margin: '0 0 20px', fontSize: '18px' }}>Forcer un gain pour le prochain joueur</h3>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: 600 }}>Session *</label>
          <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} style={selectStyle}>
            <option value="">Sélectionner une session</option>
            {sessions.map(s => <option key={s.id} value={s.id}>{s.name} - {s.pdv_name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontSize: '14px', fontWeight: 600 }}>Lot à forcer *</label>
          <select value={selectedPrize} onChange={(e) => setSelectedPrize(e.target.value)} style={selectStyle}>
            <option value="">Sélectionner un lot</option>
            {prizes.filter(p => p.prize_type !== 'try_again_1' && p.prize_type !== 'try_again_2').map(p => <option key={p.id} value={p.prize_type}>{p.prize_label}</option>)}
          </select>
        </div>
        <button onClick={handleInject} disabled={loading} style={{ width: '100%', padding: '14px', background: loading ? 'rgba(21,101,192,0.5)' : C.primary, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Injection...' : 'Injecter ce gain'}
        </button>
      </div>
    </div>
  );
};

// ============ USERS ============
const UsersContent = ({ message, setMessage }) => {
  const [users, setUsers] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [filterPdv, setFilterPdv] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/users'); setUsers(r.data); }
    catch { setMessage({ type: 'error', text: 'Erreur lors du chargement des utilisateurs' }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadUsers();
    api.get('/admin/pdv').then(r => setPdvs(r.data)).catch(() => {});
  }, []);

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setMessage({ type: 'success', text: 'Utilisateur supprimé' });
      loadUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const roleColors = {
    super_admin: { bg: '#fff3e0', color: '#e65100', label: 'Super Admin' },
    admin: { bg: '#e3f2fd', color: '#1565c0', label: 'Admin' },
    hotesse: { bg: 'rgba(82,196,26,0.12)', color: '#389e0d', label: 'Hôtesse' },
  };

  const filteredUsers = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterPdv && String(u.pdv_id) !== filterPdv) return false;
    return true;
  });

  const selectStyle = { padding: '8px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '13px', color: '#333', background: '#fff', cursor: 'pointer', outline: 'none' };

  if (loading) return <div style={{ flex: 1, padding: '30px', textAlign: 'center', color: '#333' }}>Chargement...</div>;

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-user-cog" style={{ color: '#ffffff', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>Utilisateurs</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={selectStyle}>
            <option value="">Tous les rôles</option>
            <option value="hotesse">Hôtesse</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <select value={filterPdv} onChange={(e) => setFilterPdv(e.target.value)} style={selectStyle}>
            <option value="">Tous les PDV</option>
            {pdvs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}</option>)}
          </select>
          {(filterRole || filterPdv) && (
            <button onClick={() => { setFilterRole(''); setFilterPdv(''); }} style={{ padding: '8px 14px', background: 'rgba(255,77,79,0.08)', color: '#cf1322', border: '1px solid rgba(255,77,79,0.3)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-times"></i> Réinitialiser
            </button>
          )}
          <button
            onClick={() => { setSelectedUser(null); setShowModal(true); }}
            style={{ padding: '10px 20px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <i className="fas fa-plus"></i>Nouvel Utilisateur
          </button>
        </div>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {(filterRole || filterPdv) && (
          <div style={{ marginBottom: '12px', fontSize: '13px', color: '#555' }}>
            <i className="fas fa-filter" style={{ marginRight: '6px', color: C.primary }}></i>
            {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''} trouvé{filteredUsers.length !== 1 ? 's' : ''}
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
              {['Nom complet', 'Username', 'Email', 'Rôle', 'Point de Vente', 'Statut', 'Actions'].map(h => (
                <th key={h} style={{ padding: '16px', textAlign: 'left', color: '#555', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const rc = roleColors[user.role] || { bg: '#f0f0f0', color: '#555', label: user.role };
              return (
                <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '16px', fontWeight: 600, color: '#1a1a1a' }}>{user.full_name || '-'}</td>
                  <td style={{ padding: '16px', color: '#333' }}>{user.username}</td>
                  <td style={{ padding: '16px', color: '#555', fontSize: '13px' }}>{user.email || '-'}</td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: rc.bg, color: rc.color }}>{rc.label}</span>
                  </td>
                  <td style={{ padding: '16px', color: '#333', fontSize: '13px' }}>{user.pdv_name || '-'}</td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: user.is_active ? 'rgba(82,196,26,0.12)' : 'rgba(255,77,79,0.1)', color: user.is_active ? '#389e0d' : '#cf1322' }}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setSelectedUser(user); setShowModal(true); }}
                        style={{ padding: '6px 12px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        style={{ padding: '6px 12px', background: 'rgba(255,77,79,0.08)', color: '#cf1322', border: '1px solid rgba(255,77,79,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Aucun utilisateur{filterRole || filterPdv ? ' correspondant aux filtres' : ''}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserModal
          user={selectedUser}
          onClose={() => setShowModal(false)}
          onSave={async () => { await loadUsers(); setShowModal(false); setMessage({ type: 'success', text: selectedUser ? 'Utilisateur mis à jour' : 'Utilisateur créé avec succès' }); }}
          onError={(msg) => setMessage({ type: 'error', text: msg })}
        />
      )}
    </div>
  );
};

const UserModal = ({ user, onClose, onSave, onError }) => {
  const [pdvs, setPdvs] = useState([]);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    full_name: user?.full_name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'hotesse',
    pdv_id: user?.pdv_id || '',
    is_active: user?.is_active !== undefined ? user.is_active : true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/pdv').then(r => setPdvs(r.data)).catch(() => {});
  }, []);

  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: '#fff', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#333' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user && !formData.password) { onError('Le mot de passe est obligatoire pour un nouvel utilisateur'); return; }
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.email) delete payload.email;
      if (!payload.pdv_id) delete payload.pdv_id;
      if (!payload.password) delete payload.password;
      if (payload.pdv_id) payload.pdv_id = parseInt(payload.pdv_id);

      if (user) {
        await api.put(`/admin/users/${user.id}`, payload);
      } else {
        await api.post('/admin/users', payload);
      }
      onSave();
    } catch (error) {
      onError(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>
            {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Nom complet</label>
              <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Jean Dupont" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Nom d'utilisateur *</label>
              <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="jean.dupont" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jean@example.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{user ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" required={!user} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Rôle *</label>
              <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} required style={inputStyle}>
                <option value="hotesse">Hôtesse</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Point de Vente</label>
              <select value={formData.pdv_id} onChange={(e) => setFormData({ ...formData, pdv_id: e.target.value })} style={inputStyle}>
                <option value="">Aucun</option>
                {pdvs.map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}{pdv.city ? ` (${pdv.city.name})` : ''}</option>)}
              </select>
            </div>
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <label htmlFor="is_active" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>Compte actif</label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              Annuler
            </button>
            <button type="submit" disabled={saving} style={{ flex: 1, padding: '12px', background: saving ? 'rgba(21,101,192,0.5)' : C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Enregistrement...' : (user ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
